import { ensureSession } from '@/lib/api-client';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const RECONNECT_BASE_MS = 2_000;
const RECONNECT_MAX_MS = 60_000;

export interface EventStreamOptions {
  path: string;
  events: readonly string[];
  onEvent: (event: MessageEvent<string>) => void;
  ensureAuthenticated?: (() => Promise<boolean>) | undefined;
}

/**
 * Öffnet einen Server-Sent-Events-Strom und hält ihn über Tokenwechsel hinweg offen.
 *
 * Die automatische Wiederverbindung des Browsers wird bewusst unterbunden: Nach
 * Ablauf des Zugriffstokens antwortet der Strom mit 401, und ein EventSource kann
 * die stille Erneuerung nicht selbst auslösen — er würde endlos gegen die
 * abgelaufene Sitzung verbinden. Stattdessen wird vor jedem Versuch die Sitzung
 * erneuert; bleibt sie ungültig, endet der Strom und die Anwendung leitet zur
 * Anmeldung weiter.
 */
export function subscribeToEventStream({
  path,
  events,
  onEvent,
  ensureAuthenticated = ensureSession,
}: EventStreamOptions): () => void {
  let source: EventSource | null = null;
  let reconnectTimer: number | undefined;
  let attempt = 0;
  let disposed = false;

  const listener = (event: Event): void => {
    attempt = 0;
    onEvent(event as MessageEvent<string>);
  };

  const connect = (): void => {
    if (disposed) {
      return;
    }

    const next = new EventSource(`${BASE_URL}${path}`, { withCredentials: true });
    source = next;

    for (const name of events) {
      next.addEventListener(name, listener);
    }

    next.onerror = () => {
      next.close();
      if (disposed || source !== next) {
        return;
      }
      source = null;

      attempt += 1;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attempt - 1), RECONNECT_MAX_MS);

      reconnectTimer = window.setTimeout(() => {
        void ensureAuthenticated().then((valid) => {
          if (valid) {
            connect();
          }
        });
      }, delay);
    };
  };

  connect();

  return () => {
    disposed = true;
    window.clearTimeout(reconnectTimer);
    source?.close();
  };
}
